import { Asset, DepositPosition, Nullable } from '../../interfaces';
import { Decimal } from '../../datastructures';
import { getNostraTokenFromFlags } from '../../utils';
import { walletAddressAtom } from '../wallet';
import assetsAtom from './assetsAtom';
import assetVariantAtom, { AssetVariant } from './assetVariantAtom';
import { activeAccountIdAtom } from './activeAccountIdAtom';
import assetTokenRatesAtom from './assetTokenRatesAtom';
import nostraTokenBalancesAtom, { NostraTokenBalanceMap } from './nostraTokenBalanceAtom';
import { createAsyncAtom } from '../../lib/state';

type AssetAddressPositionMap = { [walletAddressAccountIdAssetAddress: string]: DepositPosition };

const fetchData = (
  walletAddress: string,
  accountId: number,
  asset: Asset,
  collateralFactor: Decimal,
  lendingRate: Decimal,
  tokenRate: Decimal,
  assetVariant: AssetVariant,
  nostraTokenBalances: NostraTokenBalanceMap,
): Nullable<DepositPosition> => {
  try {
    const nostraToken = getNostraTokenFromFlags(asset, {
      interestBearing: assetVariant.isLending,
      collateral: assetVariant.isCollateral,
    });

    const nostraTokenBalance = nostraTokenBalances[`${walletAddress}-${accountId}-${nostraToken.address}`];

    // Still loading the nostra token balance
    if (!nostraTokenBalance) {
      return null;
    }

    const balanceUsd = nostraTokenBalance.mul(tokenRate);

    return {
      asset,
      balance: nostraTokenBalance,
      balanceUsd,
      apy: lendingRate,
      collateralFactor,
    } as DepositPosition;
  } catch (error) {
    console.error(
      `useDepositPositions - Error fetching deposit position for token ${asset.token} and account ${walletAddress}`,
      error,
    );
    return null;
  }
};

const lendPositionsAtom = createAsyncAtom(async get => {
  const assets = await get(assetsAtom);
  const assetVariants = await get(assetVariantAtom);
  const walletAddress = await get(walletAddressAtom);
  const activeAccountId = await get(activeAccountIdAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);
  const nostraTokenBalances = await get(nostraTokenBalancesAtom);

  if (!assets || !assetVariants || !walletAddress) {
    return [];
  }

  const lendTokenBalancesMap: AssetAddressPositionMap = {};
  assets.forEach(asset => {
    if (!asset.supplyApy || !asset.collateralFactor) {
      return null;
    }

    const accountAssetVariant = assetVariants[`${walletAddress}-${activeAccountId}-${asset.address}`];
    if (!accountAssetVariant) {
      return null;
    }

    const tokenRate = assetTokenRates.rate[asset.address];
    if (!tokenRate) {
      return null;
    }

    const position = fetchData(
      walletAddress,
      activeAccountId,
      asset,
      asset.collateralFactor,
      asset.supplyApy,
      tokenRate,
      accountAssetVariant,
      nostraTokenBalances,
    );

    if (!position || position.balance.isZero()) {
      return null;
    }

    lendTokenBalancesMap[`${walletAddress}-${activeAccountId}-${asset.address}`] = position;
  });

  return Object.values(lendTokenBalancesMap).sort((a, b) =>
    b.balanceUsd.gt(a.balanceUsd) ? 1 : b.balanceUsd.lt(a.balanceUsd) ? -1 : 0,
  );
});
export default lendPositionsAtom;
